<!-- Page header -->
<div class="flex flex-row justify-between items-center mb-3">
  <div class="flex flex-row items-center gap-3">
    <img
      class="left-arrow clickable"
      src="../../../../../assets/icons/left-arrow.svg"
      (click)="goBack()"
    />
    <div class="flex flex-col">
      <h1 class="headeradminui-text md:break-keep">Policy Controls</h1>
      <div class="text-xs text-gray-500" *ngIf="contract.companyName">
        {{ contract.companyName }} · Policy Configuration
      </div>
    </div>
  </div>
</div>

<nz-spin [nzSpinning]="loading" nzTip="Loading...">
  <nz-tabset>

    <!-- ── Tab 1: Contract Settings ── -->
    <nz-tab nzTitle="Contract Settings">
      <div class="custom-card mt-3">
        <div class="card-body p-4">
          <div class="flex items-start justify-between gap-4 mb-4">
            <div>
              <div class="text-base font-semibold">Contract Settings</div>
              <div class="text-sm text-gray-500 mt-1">
                Pool model and health program limits apply company-wide regardless of individual rules.
              </div>
            </div>
            <nz-tag [nzColor]="contract.policyControlsEnabled ? 'green' : 'default'">
              {{ contract.policyControlsEnabled ? 'Engine ON' : 'Engine OFF' }}
            </nz-tag>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block mb-1 font-medium">Session pool model</label>
              <nz-select [(ngModel)]="contract.policyPoolMode" class="w-full"
                         (ngModelChange)="policyEngineValidationError = ''">
                <nz-option nzValue="individual" nzLabel="Per employee (individual caps)"></nz-option>
                <nz-option nzValue="company" nzLabel="Company pool (shared)"></nz-option>
              </nz-select>
              <div class="text-xs text-gray-500 mt-1">
                Company pool shares one quota across employees; per employee gives each person their own cap.
              </div>
            </div>

            <div *ngIf="contract.policyPoolMode === 'company'">
              <label class="block mb-1 font-medium">Pool size (total sessions)</label>
              <nz-input-number
                [(ngModel)]="contract.policyPurchasedSessionPool"
                [nzMin]="0"
                class="w-full"
                (ngModelChange)="policyEngineValidationError = ''"
              ></nz-input-number>
              <div class="text-xs text-gray-500 mt-1">
                Total sessions funded by the company across all employees for the current period.
              </div>
            </div>

            <div *ngIf="contract.policyPoolMode === 'company'">
              <label class="block mb-1 font-medium">Pool-governed verticals</label>
              <nz-select
                [(ngModel)]="contract.policyPoolVerticalIds"
                nzMode="multiple"
                nzPlaceHolder="All verticals (default)"
                class="w-full"
              >
                <nz-option
                  *ngFor="let v of verticals"
                  [nzValue]="v.m_id"
                  [nzLabel]="v.data"
                ></nz-option>
              </nz-select>
              <div class="text-xs text-gray-500 mt-1">
                Only sessions in these verticals draw from the shared pool. Leave empty to apply the pool across all verticals.
              </div>
            </div>

            <div class="md:col-span-2 flex items-center justify-between gap-3 p-3 rounded bg-gray-50">
              <div>
                <div class="font-medium">Allow employee self-pay on pool exhausted</div>
                <div class="text-xs text-gray-500 mt-1">
                  If enabled, employees can continue booking by paying themselves after the company pool runs out.
                </div>
              </div>
              <nz-switch [(ngModel)]="contract.policyAllowSelfPayOnPoolExhaust"></nz-switch>
            </div>

            <div class="flex items-center justify-between gap-3 p-3 rounded bg-gray-50"
                 [style.border]="policyEngineValidationError ? '1.5px solid #ff4d4f' : 'none'">
              <div>
                <div class="font-medium">Policy engine enabled</div>
                <div class="text-xs text-gray-500 mt-1">
                  Turns on policy-driven visibility and booking controls for this company.
                </div>
                <div *ngIf="policyEngineValidationError" class="text-xs mt-1" style="color:#ff4d4f">
                  {{ policyEngineValidationError }}
                </div>
              </div>
              <nz-switch
                [ngModel]="contract.policyControlsEnabled"
                (ngModelChange)="onPolicyEngineToggle($event)"
              ></nz-switch>
            </div>

            <div class="md:col-span-2 flex items-center justify-between gap-3 p-3 rounded bg-gray-50">
              <div>
                <div class="font-medium">GST on self-pay base amount</div>
                <div class="text-xs text-gray-500 mt-1">
                  Applies to the slab rate or flat amount an employee is charged for self-pay sessions. Does not
                  cover GST on the markup — that's always applied whenever a rule sets a markup, see below.
                </div>
              </div>
              <nz-switch [(ngModel)]="contract.policySelfPayBaseGstEnabled"></nz-switch>
            </div>

            <div *ngIf="contract.policySelfPayBaseGstEnabled">
              <label class="block mb-1 font-medium">Self-pay base GST %</label>
              <nz-input-number
                [(ngModel)]="contract.policySelfPayBaseGstPercent"
                [nzMin]="0"
                [nzMax]="100"
                class="w-full"
              ></nz-input-number>
            </div>

            <div>
              <label class="block mb-1 font-medium">Self-pay markup GST %</label>
              <nz-input-number
                [(ngModel)]="contract.policySelfPayMarkupGstPercent"
                [nzMin]="0"
                [nzMax]="100"
                class="w-full"
              ></nz-input-number>
              <div class="text-xs text-gray-500 mt-1">
                GST rate on the self-pay markup. No on/off toggle — this rate applies automatically whenever a
                Booking Rule sets a markup (Session Booking rule, "Self-pay pricing" = slab, with a markup value).
                The markup amount itself is set per-rule, not here.
              </div>
            </div>

            <div class="md:col-span-2 flex items-center justify-between gap-3 p-3 rounded bg-gray-50">
              <div>
                <div class="font-medium">Anyo Goals enabled</div>
                <div class="text-xs text-gray-500 mt-1">
                  Lets this company's employees start new Anyo Goals. Off by default — existing goals for
                  users who already started one are unaffected either way.
                </div>
              </div>
              <nz-switch [(ngModel)]="contract.anyoGoalsEnabled"></nz-switch>
            </div>

            <div class="md:col-span-2 flex items-center justify-between gap-3 p-3 rounded bg-gray-50">
              <div>
                <div class="font-medium">Re-engagement pipeline enabled</div>
                <div class="text-xs text-gray-500 mt-1">
                  Lets the AI re-engagement pipeline reach out to this company's inactive employees. Off by default.
                </div>
              </div>
              <nz-switch [(ngModel)]="contract.reengageEnabled"></nz-switch>
            </div>
          </div>

          <div class="flex justify-end mt-5">
            <button nz-button nzType="primary" [nzLoading]="contractSaving" (click)="saveContractSettings()">
              Save Contract Settings
            </button>
          </div>
        </div>
      </div>
    </nz-tab>

    <!-- ── Tab 2: Vertical / Concern Visibility ── -->
    <nz-tab nzTitle="Vertical / Concern Visibility">
      <div class="custom-card mt-3">
        <div class="card-body p-4">
          <div class="flex items-start justify-between gap-4 mb-4">
            <div>
              <div class="flex items-center gap-2">
                <div class="text-base font-semibold">Vertical &amp; Concern Visibility</div>
                <nz-tag nzColor="blue">
                  {{ hiddenVerticalCount }} hidden · {{ visibilityVerticals.length }} total
                </nz-tag>
              </div>
              <div class="text-sm text-gray-500 mt-1">
                Control which therapy verticals and concerns employees of this company can see and book.
                Hiding a vertical removes it entirely; hiding a concern removes only that concern within an otherwise visible vertical.
              </div>
            </div>
            <div class="flex items-center gap-2">
              <button nz-button nzType="default" [disabled]="!visibilityDirty" (click)="discardVisibility()">
                Discard
              </button>
              <button
                nz-button
                nzType="primary"
                [disabled]="!visibilityDirty"
                [nzLoading]="visibilitySaving"
                (click)="saveVisibility()"
              >
                Save Changes
              </button>
            </div>
          </div>

          <nz-spin [nzSpinning]="visibilityLoading">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div
                *ngFor="let v of visibilityVerticals"
                class="flex items-center gap-3 p-3 rounded-xl border transition-all"
                [style.background]="v.visible ? '#eff6ff' : '#f9fafb'"
                [style.border-color]="v.visible ? '#bfdbfe' : '#e5e7eb'"
              >
                <!-- Vertical icon -->
                <div
                  class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
                  style="background:#fff"
                >
                  <img
                    *ngIf="verticalImage(v.verticalId)"
                    [src]="verticalImage(v.verticalId)"
                    class="w-7 h-7 object-contain"
                    [alt]="v.verticalName"
                  />
                  <span *ngIf="!verticalImage(v.verticalId)" class="text-lg">🏥</span>
                </div>

                <!-- Name + concern badge -->
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium truncate">{{ v.verticalName }}</div>
                  <button
                    *ngIf="(v.concerns?.length ?? 0) > 0"
                    nz-button nzType="link" nzSize="small"
                    style="padding:0;height:auto;font-size:11px;color:#6b7280"
                    (click)="openConcerns(v)"
                  >
                    <span [style.color]="hiddenConcernsCountFor(v) > 0 ? '#f97316' : '#6b7280'">
                      {{ hiddenConcernsCountFor(v) > 0 ? hiddenConcernsCountFor(v) + ' concern' + (hiddenConcernsCountFor(v) > 1 ? 's' : '') + ' hidden' : 'All concerns visible' }}
                    </span>
                    · Edit →
                  </button>
                </div>

                <!-- Toggle -->
                <nz-switch
                  [ngModel]="v.visible"
                  (ngModelChange)="toggleVerticalVisible(v, $event)"
                ></nz-switch>
              </div>
            </div>

            <div
              *ngIf="!visibilityLoading && visibilityVerticals.length === 0"
              class="text-center py-8 text-gray-400 text-sm"
            >
              No verticals found.
            </div>
          </nz-spin>
        </div>
      </div>
    </nz-tab>

    <!-- ── Tab 3: Therapist Access Rules ── -->
    <nz-tab nzTitle="Therapist Access Rules">
      <div class="custom-card mt-3">
        <div class="card-body p-4">

          <div class="flex items-start justify-between gap-4 mb-3">
            <div>
              <div class="flex items-center gap-2">
                <div class="text-base font-semibold">Therapist Access Rules</div>
                <nz-tag nzColor="blue">{{ activeTfRulesCount }} active · {{ tfRulesUI.length }} total</nz-tag>
              </div>
              <div class="text-sm text-gray-500 mt-1">
                Restrict which therapists employees can see and book. Use this to enforce a pricing band
                or limit to internal therapists. If no rules are set, all available therapists are shown.
              </div>
            </div>
            <a routerLink="/rule-engine/create">
              <button nz-button nzType="primary">+ Create Rule</button>
            </a>
          </div>

          <nz-spin [nzSpinning]="tfRulesLoading">

            <!-- Empty state -->
            <div
              *ngIf="!tfRulesLoading && tfRulesUI.length === 0"
              class="text-center py-8 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg"
            >
              No therapist filter rules yet. All available therapists are shown to employees.
              <br />
              <a routerLink="/rule-engine/create" class="text-blue-400 hover:underline text-xs mt-1 inline-block">
                Create a rule in Rule Engine →
              </a>
            </div>

            <!-- Read-only rule cards -->
            <div
              *ngFor="let rule of tfRulesUI"
              class="border rounded-lg mb-1.5 overflow-hidden"
              style="border-color:#e8e8e8"
            >
              <div class="flex items-center gap-3 px-4 py-3 min-h-[46px] hover:bg-gray-50">
                <!-- Active dot -->
                <span class="text-sm flex-shrink-0"
                  [ngClass]="rule.active ? 'text-green-500' : 'text-gray-300'"
                >●</span>

                <!-- Priority pill -->
                <span
                  class="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                  style="background:#f5f5f5;border:1px solid #e0e0e0;color:#666;min-width:32px;text-align:center"
                >P{{ rule.priority }}</span>

                <!-- Name -->
                <span class="flex-1 text-sm font-medium">{{ rule.name || 'Unnamed rule' }}</span>

                <!-- Summary chips -->
                <span *ngIf="rule.maxRate !== null && rule.maxRate !== undefined"
                  class="text-xs px-2 py-0.5 rounded flex-shrink-0"
                  style="background:#f5f5f5;color:#555"
                >≤ ₹{{ rule.maxRate }}</span>

                <nz-tag *ngIf="rule.internalOnly" nzColor="orange" style="margin:0;flex-shrink:0">
                  Internal only
                </nz-tag>

                <ng-container *ngIf="rule.excludeTags.length > 0">
                  <nz-tag
                    *ngFor="let tag of rule.excludeTags.slice(0, 2)"
                    style="margin:0 2px;flex-shrink:0"
                  >{{ tag }}</nz-tag>
                  <span *ngIf="rule.excludeTags.length > 2" class="text-xs text-gray-400 flex-shrink-0">
                    +{{ rule.excludeTags.length - 2 }}
                  </span>
                </ng-container>

                <!-- Active badge -->
                <nz-tag [nzColor]="rule.active ? 'green' : 'default'" style="margin:0 0 0 4px;flex-shrink:0">
                  {{ rule.active ? 'Active' : 'Inactive' }}
                </nz-tag>

                <!-- Edit link → existing rule engine -->
                <a
                  *ngIf="rule.id"
                  [routerLink]="['/rule-engine/create', rule.id]"
                  class="text-xs text-blue-400 hover:text-blue-600 flex-shrink-0 ml-2"
                >Edit →</a>
              </div>
            </div>

          </nz-spin>
        </div>
      </div>
    </nz-tab>

    <!-- ── Tab 4: Booking Rules ── -->
    <nz-tab nzTitle="Booking Rules">
      <div class="custom-card mt-3">
        <div class="card-body p-4">

          <div class="flex items-start justify-between gap-4 mb-3">
            <div>
              <div class="flex items-center gap-2">
                <div class="text-base font-semibold">Booking Rules</div>
                <nz-tag nzColor="blue">{{ activePolicyRulesCount }} active · {{ bookingRulesUI.length }} total</nz-tag>
              </div>
              <div class="text-sm text-gray-500 mt-1">
                Rules are evaluated by priority — highest number wins per output.
                Global rules are platform defaults; company rules override them.
              </div>
            </div>
          </div>

          <nz-spin [nzSpinning]="policyRulesLoading">

            <!-- ── Personal rules group (read-only — created/edited only from User Profile) ── -->
            <div class="flex items-center gap-2 mb-2 mt-1">
              <span class="text-xs font-bold uppercase tracking-wider" style="color:#9ca3af">
                Personal rules — per-employee overrides, checked first (read-only)
              </span>
              <div class="flex-1" style="height:1px;background:#e8e8e8"></div>
            </div>
            <div class="text-xs text-gray-400 mb-2">
              Created and edited from an employee's User Profile screen, not here. Shown for context only — these
              always win over the company/global rules above for whatever output field they set.
            </div>

            <div
              *ngIf="!policyRulesLoading && personalRulesUI.length === 0"
              class="text-center py-4 text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg mb-2"
            >
              No personal rules for any employee at this company yet.
            </div>

            <ng-container *ngIf="personalRulesUI.length > 0">
              <div
                *ngFor="let rule of personalRulesUI"
                class="border rounded-lg mb-1.5 overflow-hidden"
                style="border-color:#e8e8e8;background:#fafafa;opacity:0.9"
              >
                <div class="flex items-center gap-2 px-4 py-2.5 select-none min-h-[46px]">
                  <span
                    class="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                    style="background:#f0f0f0;border:1px solid #e0e0e0;color:#aaa;min-width:32px;text-align:center"
                  >P{{ rule.priority }}</span>
                  <nz-tag nzColor="magenta" style="margin:0;flex-shrink:0">Personal</nz-tag>
                  <span
                    class="text-xs font-mono px-2 py-0.5 rounded flex-shrink-0"
                    style="background:#f5f5f5;color:#555"
                    title="User ID"
                  >{{ rule.userId }}</span>
                  <span
                    class="text-xs font-medium px-2 py-0.5 rounded flex-shrink-0"
                    style="background:#f5f5f5;color:#555"
                  >{{ rule.action }}</span>
                  <span class="text-xs font-medium text-gray-500 truncate" style="max-width:120px">
                    {{ scopeSummary(rule) }}
                  </span>
                  <span class="text-gray-300 flex-shrink-0">·</span>
                  <span
                    class="text-xs px-2 py-0.5 rounded flex-shrink-0 truncate"
                    style="background:#f9fafb;border:1px solid #e8ecf4;color:#6b7280;max-width:180px"
                  >{{ condSummary(rule) }}</span>
                  <span class="text-gray-300 flex-shrink-0">→</span>
                  <span
                    class="text-xs px-2 py-0.5 rounded flex-shrink-0 truncate"
                    style="background:#f0fdf4;border:1px solid #bbf7d0;color:#166534;max-width:200px"
                  >{{ thenSummary(rule) }}</span>
                  <div class="flex-1"></div>
                  <nz-tag [nzColor]="rule.active ? 'green' : 'default'" style="margin:0;flex-shrink:0">
                    {{ rule.active ? 'Active' : 'Off' }}
                  </nz-tag>
                </div>
              </div>
            </ng-container>

            <!-- ── Company rules group (editable) ── -->
            <div class="flex items-center gap-2 mb-2" [ngClass]="personalRulesUI.length > 0 ? 'mt-4' : 'mt-1'">
              <span class="text-xs font-bold uppercase tracking-wider" style="color:#9ca3af">
                Company rules — checked next, override global with higher priority
              </span>
              <div class="flex-1" style="height:1px;background:#e8e8e8"></div>
            </div>

            <!-- Empty state (company rules only) -->
            <div
              *ngIf="!policyRulesLoading && companyRulesUI.length === 0"
              class="text-center py-6 text-sm border border-gray-200 rounded-lg bg-white mb-2"
              style="color:#9ca3af"
            >
              No company rules yet. Add one below — use priority &gt; {{ globalRulesUI.length > 0 ? globalRulesUI[0].priority : 0 }} to override global defaults.
            </div>

            <!-- Company rule cards (editable) -->
            <div
              *ngFor="let rule of companyRulesUI"
              class="border rounded-lg mb-1.5 overflow-hidden transition-colors"
              [style.border-color]="rule.expanded ? '#1890ff' : '#e8e8e8'"
            >
              <!-- Collapsed header — click to expand -->
              <div
                class="flex items-center gap-2 px-4 py-2.5 cursor-pointer select-none min-h-[46px]"
                [ngClass]="rule.expanded ? 'bg-blue-50' : 'hover:bg-gray-50'"
                (click)="toggleRuleExpand(rule)"
              >
                <!-- Priority pill -->
                <span
                  class="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                  style="background:#f5f5f5;border:1px solid #e0e0e0;color:#666;min-width:32px;text-align:center"
                >P{{ rule.priority }}</span>

                <!-- Action badge -->
                <span
                  class="text-xs font-medium px-2 py-0.5 rounded flex-shrink-0"
                  style="background:#f5f5f5;color:#555"
                >{{ rule.action }}</span>

                <!-- Scope -->
                <span class="text-xs font-medium text-gray-700 truncate" style="max-width:120px">
                  {{ scopeSummary(rule) }}
                </span>
                <span class="text-gray-300 flex-shrink-0">·</span>

                <!-- Condition summary -->
                <span
                  class="text-xs px-2 py-0.5 rounded flex-shrink-0 truncate"
                  style="background:#f9fafb;border:1px solid #e8ecf4;color:#6b7280;max-width:180px"
                >{{ condSummary(rule) }}</span>
                <span class="text-gray-300 flex-shrink-0">→</span>

                <!-- Then summary -->
                <span
                  class="text-xs px-2 py-0.5 rounded flex-shrink-0 truncate"
                  style="background:#f0fdf4;border:1px solid #bbf7d0;color:#166534;max-width:200px"
                >{{ thenSummary(rule) }}</span>

                <div class="flex-1"></div>

                <!-- Active badge -->
                <nz-tag [nzColor]="rule.active ? 'green' : 'default'" style="margin:0;flex-shrink:0">
                  {{ rule.active ? 'Active' : 'Off' }}
                </nz-tag>

                <!-- Delete -->
                <button
                  nz-button nzType="link" nzDanger nzSize="small"
                  style="padding:0 4px;flex-shrink:0"
                  (click)="$event.stopPropagation(); deleteRule(rule)"
                >✕</button>

                <!-- Chevron -->
                <span class="text-gray-400 text-xs flex-shrink-0 ml-1">
                  {{ rule.expanded ? '▲' : '▼' }}
                </span>
              </div>

              <!-- Expanded body -->
              <div *ngIf="rule.expanded" class="border-t border-gray-100">

                <!-- FOR zone -->
                <div class="p-4 border-b border-gray-100">
                  <div class="text-xs font-bold uppercase tracking-wider mb-3" style="color:#9ca3af">
                    FOR — what does this rule apply to?
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label class="block mb-1 text-xs text-gray-500 font-medium">Action</label>
                      <nz-select [(ngModel)]="rule.action" class="w-full" (ngModelChange)="onRuleActionChange(rule)">
                        <nz-option *ngFor="let a of ACTIONS" [nzValue]="a" [nzLabel]="a"></nz-option>
                      </nz-select>
                    </div>
                    <ng-container *ngIf="rule.action === 'Session Booking'">
                      <div>
                        <label class="block mb-1 text-xs text-gray-500 font-medium">
                          Vertical <span class="text-gray-300 italic font-normal">optional</span>
                        </label>
                        <nz-select [(ngModel)]="rule.scope.verticalId" class="w-full" nzMode="multiple" nzAllowClear nzPlaceholder="Any vertical" (ngModelChange)="onScopeChange(rule)">
                          <nz-option *ngFor="let v of verticals" [nzValue]="v.m_id" [nzLabel]="v.data"></nz-option>
                        </nz-select>
                      </div>
                      <div>
                        <label class="block mb-1 text-xs text-gray-500 font-medium">
                          Concern <span class="text-gray-300 italic font-normal">optional</span>
                        </label>
                        <nz-select [(ngModel)]="rule.scope.concernId" class="w-full" nzMode="multiple" nzAllowClear nzPlaceholder="Any concern" nzShowSearch (ngModelChange)="onScopeChange(rule)">
                          <nz-option *ngFor="let c of concernsForRule(rule)" [nzValue]="c.m_id" [nzLabel]="c.data"></nz-option>
                        </nz-select>
                      </div>
                      <div>
                        <label class="block mb-1 text-xs text-gray-500 font-medium">
                          Expert level <span class="text-gray-300 italic font-normal">optional</span>
                        </label>
                        <nz-select [(ngModel)]="rule.scope.expertLevelId" class="w-full" nzAllowClear nzPlaceholder="Any level">
                          <nz-option *ngFor="let e of expertLevels" [nzValue]="e.m_id" [nzLabel]="e.data"></nz-option>
                        </nz-select>
                      </div>
                      <div>
                        <label class="block mb-1 text-xs text-gray-500 font-medium">
                          Experts <span class="text-gray-300 italic font-normal">optional — one or more named therapists, narrower than expert level</span>
                        </label>
                        <nz-select [(ngModel)]="rule.scope.therapistIds" class="w-full" nzMode="multiple" nzAllowClear nzShowSearch nzPlaceholder="Any expert">
                          <nz-option *ngFor="let t of therapists" [nzValue]="t._id" [nzLabel]="therapistDisplayName(t._id)"></nz-option>
                        </nz-select>
                      </div>
                    </ng-container>
                    <ng-container *ngIf="rule.action === 'Benefit Visibility'">
                      <div>
                        <label class="block mb-1 text-xs text-gray-500 font-medium">
                          Benefit <span class="text-gray-300 italic font-normal">optional</span>
                        </label>
                        <nz-select [(ngModel)]="rule.scope.benefitCategory" class="w-full" nzAllowClear nzPlaceholder="All benefits">
                          <nz-option
                            *ngFor="let b of ALL_CONTRACT_BENEFITS"
                            [nzValue]="b"
                            [nzLabel]="BENEFIT_META[b].icon + ' ' + BENEFIT_META[b].label"
                          ></nz-option>
                        </nz-select>
                      </div>
                    </ng-container>
                    <div>
                      <label class="block mb-1 text-xs text-gray-500 font-medium">Priority</label>
                      <nz-input-number [(ngModel)]="rule.priority" [nzMin]="0" class="w-full"></nz-input-number>
                      <div class="text-xs text-gray-400 mt-1">10 = default, 100+ = override</div>
                    </div>
                    <div class="flex items-center justify-between gap-2 p-3 rounded" style="background:#f9fafb">
                      <span class="text-sm font-medium">Active</span>
                      <nz-switch [(ngModel)]="rule.active"></nz-switch>
                    </div>
                    <div class="md:col-span-2">
                      <label class="block mb-1 text-xs text-gray-500 font-medium">Rule name</label>
                      <input nz-input [(ngModel)]="rule.name" placeholder="e.g. Default corporate sessions" />
                    </div>
                  </div>
                </div>

                <!-- WHEN zone -->
                <div class="p-4 border-b border-gray-100">
                  <div class="text-xs font-bold uppercase tracking-wider mb-3" style="color:#9ca3af">
                    WHEN — condition
                  </div>

                  <!-- Mode toggle buttons -->
                  <div class="flex gap-2 mb-3">
                    <button
                      class="px-3 py-1 rounded text-xs cursor-pointer border transition-all"
                      [style.border-color]="rule.conditionMode === 'always' ? '#1890ff' : '#d9d9d9'"
                      [style.color]="rule.conditionMode === 'always' ? '#1890ff' : '#6b7280'"
                      [style.background]="rule.conditionMode === 'always' ? '#e6f4ff' : '#fff'"
                      [style.font-weight]="rule.conditionMode === 'always' ? '500' : '400'"
                      (click)="rule.conditionMode = 'always'"
                    >Always applies (no conditions)</button>
                    <button
                      class="px-3 py-1 rounded text-xs cursor-pointer border transition-all"
                      [style.border-color]="rule.conditionMode === 'when' ? '#1890ff' : '#d9d9d9'"
                      [style.color]="rule.conditionMode === 'when' ? '#1890ff' : '#6b7280'"
                      [style.background]="rule.conditionMode === 'when' ? '#e6f4ff' : '#fff'"
                      [style.font-weight]="rule.conditionMode === 'when' ? '500' : '400'"
                      (click)="rule.conditionMode = 'when'"
                    >Only when… (set conditions below)</button>
                  </div>

                  <ng-container *ngIf="rule.conditionMode === 'when'">
                    <!-- Condition rows -->
                    <div *ngFor="let cond of rule.condRows; let i = index" class="flex items-center gap-2 mb-2">
                      <nz-select
                        [(ngModel)]="cond.fact"
                        class="flex-1"
                        [nzDropdownMatchSelectWidth]="false"
                        nzPlaceHolder="— select fact —"
                        (ngModelChange)="onFactChange(rule, cond)"
                      >
                        <nz-option *ngFor="let f of factsFor(rule.action, rule)" nzCustomContent [nzValue]="f.value" [nzLabel]="f.label">
                          <div style="pointer-events:none">{{ f.label }}</div>
                          <small *ngIf="f.description" style="color:#8c8c8c;white-space:normal;line-height:1.3;display:block;max-width:320px;pointer-events:none">{{ f.description }}</small>
                        </nz-option>
                      </nz-select>

                      <ng-container *ngIf="cond.fact">
                        <!-- Days param input for parameterized facts -->
                        <ng-container *ngIf="cond.fact === 'sessionsInLastNDays' || cond.fact === 'totalSessionsInLastNDays'">
                          <span class="text-xs text-gray-500 flex-shrink-0">in last</span>
                          <nz-input-number [(ngModel)]="cond.params!['days']" [nzMin]="1" style="width:72px;flex-shrink:0" nzPlaceHolder="7"></nz-input-number>
                          <span class="text-xs text-gray-500 flex-shrink-0">days</span>
                        </ng-container>
                        <!-- Boolean fact -->
                        <ng-container *ngIf="factType(rule.action, cond.fact) === 'bool'">
                          <span class="text-xs text-gray-500 flex-shrink-0">is</span>
                          <nz-select [(ngModel)]="cond.value" style="width:80px;flex-shrink:0">
                            <nz-option [nzValue]="true" nzLabel="Yes"></nz-option>
                            <nz-option [nzValue]="false" nzLabel="No"></nz-option>
                          </nz-select>
                        </ng-container>
                        <!-- Numeric fact -->
                        <ng-container *ngIf="factType(rule.action, cond.fact) === 'number'">
                          <nz-select [(ngModel)]="cond.operator" style="width:68px;flex-shrink:0">
                            <nz-option *ngFor="let op of NUM_OPS" [nzValue]="op.value" [nzLabel]="op.label">
                            </nz-option>
                          </nz-select>
                          <nz-input-number [(ngModel)]="cond.value" style="width:78px;flex-shrink:0">
                          </nz-input-number>
                        </ng-container>
                      </ng-container>

                      <button
                        nz-button nzType="link" nzDanger nzSize="small"
                        style="padding:0 4px;flex-shrink:0"
                        (click)="removeCond(rule, i)"
                      >✕</button>
                    </div>

                    <!-- Logic selector (shown when > 1 condition) -->
                    <div *ngIf="rule.condRows.length > 1" class="flex items-center gap-2 my-2">
                      <span class="text-xs text-gray-500">Match</span>
                      <nz-select [(ngModel)]="rule.condLogic" style="width:200px">
                        <nz-option nzValue="all" nzLabel="ALL conditions (AND)"></nz-option>
                        <nz-option nzValue="any" nzLabel="ANY condition (OR)"></nz-option>
                      </nz-select>
                    </div>

                    <button nz-button nzType="link" nzSize="small" (click)="addCond(rule)">
                      + Add condition
                    </button>
                  </ng-container>
                </div>

                <!-- THEN zone -->
                <div class="p-4">
                  <div class="text-xs font-bold uppercase tracking-wider mb-3" style="color:#9ca3af">
                    THEN — outputs
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div *ngFor="let out of outputsFor(rule.action)" [class.md:col-span-2]="out.type === 'text'" [hidden]="!isFieldVisible(rule, out)">
                      <label class="block mb-1 text-xs font-medium" [class.text-gray-300]="isOutputDisabled(rule, out)" [class.text-gray-500]="!isOutputDisabled(rule, out)">{{ out.label }}</label>
                      <nz-select
                        *ngIf="out.type === 'select'"
                        [(ngModel)]="rule.outputs[out.key]"
                        class="w-full"
                        nzPlaceHolder="— not set —"
                        [nzDisabled]="isOutputDisabled(rule, out)"
                        (ngModelChange)="out.key === 'sessionAllowed' || out.key === 'eventAllowed' || out.key === 'diagAllowed' ? onAllowedFieldChange(rule) : onSelfPayOutputChange(rule, out.key)"
                      >
                        <nz-option nzValue="" nzLabel="— not set —"></nz-option>
                        <nz-option *ngFor="let opt of out.options" [nzValue]="opt.v" [nzLabel]="opt.l">
                        </nz-option>
                      </nz-select>
                      <nz-input-number
                        *ngIf="out.type === 'number'"
                        [(ngModel)]="rule.outputs[out.key]"
                        class="w-full"
                        nzPlaceholder="not set"
                        [nzDisabled]="isOutputDisabled(rule, out)"
                      ></nz-input-number>
                      <input
                        *ngIf="out.type === 'text'"
                        nz-input
                        [(ngModel)]="rule.outputs[out.key]"
                        placeholder="e.g. Your company's therapy sessions for this quarter have been used up."
                        [disabled]="isOutputDisabled(rule, out)"
                      />
                    </div>
                  </div>

                  <!-- Flat-slab price-by-duration row editor — only when payer=user and pricingMode=flatSlab -->
                  <div *ngIf="showFlatSlabEditor(rule)" class="mt-3 pt-3 border-t border-gray-100">
                    <div class="text-xs font-medium text-gray-500 mb-2">Price by session duration (minutes)</div>
                    <div *ngFor="let row of flatSlabRows(rule); let i = index" class="flex items-center gap-2 mb-2">
                      <nz-input-number [(ngModel)]="row.minDuration" [nzMin]="0" style="width:110px" nzPlaceHolder="min (incl.)"></nz-input-number>
                      <span class="text-xs text-gray-400">to</span>
                      <nz-input-number [(ngModel)]="row.maxDuration" [nzMin]="0" style="width:110px" nzPlaceHolder="max (excl.)"></nz-input-number>
                      <span class="text-xs text-gray-400">min →</span>
                      <nz-input-number [(ngModel)]="row.amount" [nzMin]="0" style="width:120px" nzPlaceHolder="₹ amount"></nz-input-number>
                      <button nz-button nzType="link" nzDanger nzSize="small" style="padding:0 4px" (click)="removeFlatSlabRow(rule, i)">✕</button>
                    </div>
                    <button nz-button nzType="link" nzSize="small" (click)="addFlatSlabRow(rule)">+ Add duration price</button>
                  </div>

                  <!-- Description zone -->
                  <div class="mt-4 pt-3 border-t border-gray-100">
                    <div class="flex items-start justify-between gap-3 mb-2">
                      <div class="flex items-center gap-2">
                        <span class="text-xs font-bold uppercase tracking-wider" style="color:#9ca3af">
                          Description
                        </span>
                        <span class="text-xs text-gray-400">
                          {{ rule.description ? 'Generated by AI' : 'Auto-generate a plain-English summary' }}
                        </span>
                      </div>
                      <button
                        nz-button nzType="default" nzSize="small"
                        (click)="generateDescription(rule)"
                        [nzLoading]="rule.saving && !rule.id"
                        [disabled]="rule.saving"
                      >
                        {{ rule.description ? 'Regenerate' : 'Generate' }}
                      </button>
                    </div>

                    <div *ngIf="rule.description; else noDesc" class="text-sm text-gray-600 bg-gray-50 p-3 rounded border" style="border-color:#e8e8e8">
                      {{ rule.description }}
                    </div>
                    <ng-template #noDesc>
                      <div class="text-sm text-gray-400 bg-gray-50 p-3 rounded border-dashed" style="border-color:#d9d9d9">
                        No description yet. Click Generate to create one from the rule definition.
                      </div>
                    </ng-template>
                  </div>

                  <div class="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-100">
                    <button nz-button nzType="default" (click)="rule.expanded = false">Cancel</button>
                    <button nz-button nzType="primary" [nzLoading]="rule.saving" (click)="saveRule(rule)">
                      Save Rule
                    </button>
                  </div>
                </div>

              </div><!-- end expanded body -->
            </div><!-- end rule card -->

            <!-- Add override rule button -->
            <button
              class="w-full mt-2 py-2 px-4 border-dashed border rounded-lg text-sm text-gray-400 cursor-pointer hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
              style="border-color:#d9d9d9; background:transparent"
              (click)="addRule()"
            >
              + Add booking rule
            </button>

            <!-- ── Global rules group (read-only) ── -->
            <div class="flex items-center gap-2 mb-2" [ngClass]="companyRulesUI.length > 0 ? 'mt-4' : 'mt-1'">
              <span class="text-xs font-bold uppercase tracking-wider" style="color:#9ca3af">
                Global rules — platform defaults (checked last — the fallback when nothing more specific applies)
              </span>
              <div class="flex-1" style="height:1px;background:#e8e8e8"></div>
              <a routerLink="/rule-engine/global-policy" class="text-xs text-blue-500 hover:underline flex-shrink-0">
                Manage global rules →
              </a>
            </div>

            <div
              *ngIf="!policyRulesLoading && globalRulesUI.length === 0"
              class="text-center py-4 text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg mb-2"
            >
              No global rules yet. <a routerLink="/rule-engine/global-policy" class="text-blue-400 hover:underline">Add global rules →</a>
            </div>

            <ng-container *ngIf="globalRulesUI.length > 0">
              <div
                *ngFor="let rule of globalRulesUI"
                class="border rounded-lg mb-1.5 overflow-hidden"
                style="border-color:#e8e8e8;background:#fafafa;opacity:0.85"
              >
                <!-- Read-only header -->
                <div class="flex items-center gap-2 px-4 py-2.5 select-none min-h-[46px]">
                  <span
                    class="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                    style="background:#f0f0f0;border:1px solid #e0e0e0;color:#aaa;min-width:32px;text-align:center"
                  >P{{ rule.priority }}</span>
                  <nz-tag nzColor="purple" style="margin:0;flex-shrink:0">Global</nz-tag>
                  <span
                    class="text-xs font-medium px-2 py-0.5 rounded flex-shrink-0"
                    style="background:#f5f5f5;color:#555"
                  >{{ rule.action }}</span>
                  <span class="text-xs font-medium text-gray-500 truncate" style="max-width:120px">
                    {{ scopeSummary(rule) }}
                  </span>
                  <span class="text-gray-300 flex-shrink-0">·</span>
                  <span
                    class="text-xs px-2 py-0.5 rounded flex-shrink-0 truncate"
                    style="background:#f9fafb;border:1px solid #e8ecf4;color:#6b7280;max-width:180px"
                  >{{ condSummary(rule) }}</span>
                  <span class="text-gray-300 flex-shrink-0">→</span>
                  <span
                    class="text-xs px-2 py-0.5 rounded flex-shrink-0 truncate"
                    style="background:#f0fdf4;border:1px solid #bbf7d0;color:#166534;max-width:200px"
                  >{{ thenSummary(rule) }}</span>
                  <div class="flex-1"></div>
                  <nz-tag [nzColor]="rule.active ? 'green' : 'default'" style="margin:0;flex-shrink:0">
                    {{ rule.active ? 'Active' : 'Off' }}
                  </nz-tag>
                  <a
                    routerLink="/rule-engine/global-policy"
                    class="text-xs text-blue-400 hover:text-blue-600 flex-shrink-0 ml-2"
                    (click)="$event.stopPropagation()"
                  >Edit →</a>
                </div>
              </div>
            </ng-container>

          </nz-spin>
        </div>
      </div>
    </nz-tab>

    <!-- ── Tab 5: Contract Benefits ── -->
    <nz-tab nzTitle="Contract Benefits">
      <div class="custom-card mt-3">
        <div class="card-body p-4">
          <div class="flex items-start justify-between gap-4 mb-4">
            <div>
              <div class="flex items-center gap-2">
                <div class="text-base font-semibold">Contract Benefits</div>
                <nz-tag nzColor="blue">{{ activeBenefitCount }} of {{ ALL_CONTRACT_BENEFITS.length }} active</nz-tag>
              </div>
              <div class="text-sm text-gray-500 mt-1">
                Turn benefits ON or OFF for this company. A benefit that is OFF is completely hidden from
                employees — no booking, no visibility. Pool model and health program limits apply
                company-wide regardless of individual rules.
              </div>
            </div>
          </div>

          <nz-spin [nzSpinning]="benefitVisibilityLoading">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div
                *ngFor="let b of ALL_CONTRACT_BENEFITS"
                class="flex items-center gap-3 p-3 rounded-xl border transition-all"
                [style.background]="isBenefitEnabled(b) ? '#eff6ff' : '#f9fafb'"
                [style.border-color]="isBenefitEnabled(b) ? '#bfdbfe' : '#e5e7eb'"
              >
                <div
                  class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-xl"
                  style="background:#fff"
                >
                  {{ BENEFIT_META[b].icon }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium truncate">{{ BENEFIT_META[b].label }}</div>
                  <div class="text-xs mt-0.5" [style.color]="isBenefitEnabled(b) ? '#3b82f6' : '#9ca3af'">
                    {{ isBenefitEnabled(b) ? 'Active' : 'Off' }}
                  </div>
                </div>
                <nz-spin [nzSpinning]="!!benefitSaving[b]" nzSize="small">
                  <nz-switch
                    [ngModel]="isBenefitEnabled(b)"
                    (ngModelChange)="toggleBenefit(b, $event)"
                  ></nz-switch>
                </nz-spin>
              </div>
            </div>

            <!-- ── Benefit Visibility rules (inline rule engine) ── -->
            <div class="mt-6">
              <div class="flex items-center gap-2 mb-2">
                <span class="text-xs font-bold uppercase tracking-wider" style="color:#9ca3af">
                  Visibility rules — granular overrides per benefit
                </span>
                <div class="flex-1" style="height:1px;background:#e8e8e8"></div>
              </div>
              <div class="text-xs text-gray-400 mb-3">
                Add rules to override per-user conditions — e.g. hide Diagnostics for family members while keeping it active for primary employees.
              </div>

              <!-- Global benefit-visibility rules (read-only) -->
              <ng-container *ngIf="benefitGlobalRulesUI.length > 0">
                <div class="flex items-center gap-2 mb-2">
                  <span class="text-xs font-semibold uppercase tracking-wider" style="color:#c0c0c0">Global defaults</span>
                  <div class="flex-1" style="height:1px;background:#f0f0f0"></div>
                  <a routerLink="/rule-engine/global-policy" class="text-xs text-blue-500 hover:underline flex-shrink-0">Manage →</a>
                </div>
                <div
                  *ngFor="let rule of benefitGlobalRulesUI"
                  class="border rounded-lg mb-1.5 overflow-hidden"
                  style="border-color:#e8e8e8;background:#fafafa;opacity:0.85"
                >
                  <div class="flex items-center gap-2 px-4 py-2.5 select-none min-h-[46px]">
                    <span class="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0" style="background:#f0f0f0;border:1px solid #e0e0e0;color:#aaa;min-width:32px;text-align:center">P{{ rule.priority }}</span>
                    <nz-tag nzColor="purple" style="margin:0;flex-shrink:0">Global</nz-tag>
                    <span class="text-xs font-medium text-gray-500 truncate" style="max-width:140px">{{ scopeSummary(rule) }}</span>
                    <span class="text-gray-300 flex-shrink-0">·</span>
                    <span class="text-xs px-2 py-0.5 rounded flex-shrink-0 truncate" style="background:#f9fafb;border:1px solid #e8ecf4;color:#6b7280;max-width:180px">{{ condSummary(rule) }}</span>
                    <span class="text-gray-300 flex-shrink-0">→</span>
                    <span class="text-xs px-2 py-0.5 rounded flex-shrink-0 truncate" style="background:#f0fdf4;border:1px solid #bbf7d0;color:#166534;max-width:180px">{{ thenSummary(rule) }}</span>
                    <div class="flex-1"></div>
                    <nz-tag [nzColor]="rule.active ? 'green' : 'default'" style="margin:0;flex-shrink:0">{{ rule.active ? 'Active' : 'Off' }}</nz-tag>
                    <a routerLink="/rule-engine/global-policy" class="text-xs text-blue-400 hover:text-blue-600 flex-shrink-0 ml-2" (click)="$event.stopPropagation()">Edit →</a>
                  </div>
                </div>
              </ng-container>

              <!-- Company benefit-visibility rules (editable) -->
              <div
                *ngFor="let rule of benefitCompanyRulesUI"
                class="border rounded-lg mb-1.5 overflow-hidden transition-colors"
                [style.border-color]="rule.expanded ? '#1890ff' : '#e8e8e8'"
              >
                <!-- Collapsed header -->
                <div
                  class="flex items-center gap-2 px-4 py-2.5 cursor-pointer select-none min-h-[46px]"
                  [ngClass]="rule.expanded ? 'bg-blue-50' : 'hover:bg-gray-50'"
                  (click)="toggleRuleExpand(rule)"
                >
                  <span class="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0" style="background:#f5f5f5;border:1px solid #e0e0e0;color:#666;min-width:32px;text-align:center">P{{ rule.priority }}</span>
                  <span class="text-xs font-medium text-gray-700 truncate" style="max-width:140px">{{ scopeSummary(rule) }}</span>
                  <span class="text-gray-300 flex-shrink-0">·</span>
                  <span class="text-xs px-2 py-0.5 rounded flex-shrink-0 truncate" style="background:#f9fafb;border:1px solid #e8ecf4;color:#6b7280;max-width:180px">{{ condSummary(rule) }}</span>
                  <span class="text-gray-300 flex-shrink-0">→</span>
                  <span class="text-xs px-2 py-0.5 rounded flex-shrink-0 truncate" style="background:#f0fdf4;border:1px solid #bbf7d0;color:#166534;max-width:180px">{{ thenSummary(rule) }}</span>
                  <div class="flex-1"></div>
                  <nz-tag [nzColor]="rule.active ? 'green' : 'default'" style="margin:0;flex-shrink:0">{{ rule.active ? 'Active' : 'Off' }}</nz-tag>
                  <button nz-button nzType="link" nzDanger nzSize="small" style="padding:0 4px;flex-shrink:0" (click)="$event.stopPropagation(); deleteRule(rule)">✕</button>
                  <span class="text-gray-400 text-xs flex-shrink-0 ml-1">{{ rule.expanded ? '▲' : '▼' }}</span>
                </div>

                <!-- Expanded body -->
                <div *ngIf="rule.expanded" class="border-t border-gray-100">

                  <!-- FOR zone -->
                  <div class="p-4 border-b border-gray-100">
                    <div class="text-xs font-bold uppercase tracking-wider mb-3" style="color:#9ca3af">FOR — scope</div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label class="block mb-1 text-xs text-gray-500 font-medium">Benefit <span class="text-gray-300 italic font-normal">optional</span></label>
                        <nz-select [(ngModel)]="rule.scope.benefitCategory" class="w-full" nzAllowClear nzPlaceholder="All benefits">
                          <nz-option *ngFor="let b of ALL_CONTRACT_BENEFITS" [nzValue]="b" [nzLabel]="BENEFIT_META[b].icon + ' ' + BENEFIT_META[b].label"></nz-option>
                        </nz-select>
                      </div>
                      <div>
                        <label class="block mb-1 text-xs text-gray-500 font-medium">Priority</label>
                        <nz-input-number [(ngModel)]="rule.priority" [nzMin]="0" class="w-full"></nz-input-number>
                        <div class="text-xs text-gray-400 mt-1">10 = default, 100+ = override</div>
                      </div>
                      <div class="flex items-center justify-between gap-2 p-3 rounded" style="background:#f9fafb">
                        <span class="text-sm font-medium">Active</span>
                        <nz-switch [(ngModel)]="rule.active"></nz-switch>
                      </div>
                      <div class="md:col-span-2">
                        <label class="block mb-1 text-xs text-gray-500 font-medium">Rule name</label>
                        <input nz-input [(ngModel)]="rule.name" placeholder="e.g. Hide diagnostics for family" />
                      </div>
                    </div>
                  </div>

                  <!-- WHEN zone -->
                  <div class="p-4 border-b border-gray-100">
                    <div class="text-xs font-bold uppercase tracking-wider mb-3" style="color:#9ca3af">WHEN — condition</div>
                    <div class="flex gap-2 mb-3">
                      <button class="px-3 py-1 rounded text-xs cursor-pointer border transition-all"
                        [style.border-color]="rule.conditionMode === 'always' ? '#1890ff' : '#d9d9d9'"
                        [style.color]="rule.conditionMode === 'always' ? '#1890ff' : '#6b7280'"
                        [style.background]="rule.conditionMode === 'always' ? '#e6f4ff' : '#fff'"
                        [style.font-weight]="rule.conditionMode === 'always' ? '500' : '400'"
                        (click)="rule.conditionMode = 'always'">Always applies</button>
                      <button class="px-3 py-1 rounded text-xs cursor-pointer border transition-all"
                        [style.border-color]="rule.conditionMode === 'when' ? '#1890ff' : '#d9d9d9'"
                        [style.color]="rule.conditionMode === 'when' ? '#1890ff' : '#6b7280'"
                        [style.background]="rule.conditionMode === 'when' ? '#e6f4ff' : '#fff'"
                        [style.font-weight]="rule.conditionMode === 'when' ? '500' : '400'"
                        (click)="rule.conditionMode = 'when'">Only when…</button>
                    </div>
                    <ng-container *ngIf="rule.conditionMode === 'when'">
                      <div *ngFor="let cond of rule.condRows; let i = index" class="flex items-center gap-2 mb-2">
                        <nz-select [(ngModel)]="cond.fact" class="flex-1" [nzDropdownMatchSelectWidth]="false" nzPlaceHolder="— select fact —" (ngModelChange)="onFactChange(rule, cond)">
                          <nz-option *ngFor="let f of factsFor(rule.action, rule)" nzCustomContent [nzValue]="f.value" [nzLabel]="f.label">
                            <div>{{ f.label }}</div>
                            <small *ngIf="f.description" style="color:#8c8c8c;white-space:normal;line-height:1.3;display:block;max-width:320px;pointer-events:none">{{ f.description }}</small>
                          </nz-option>
                        </nz-select>
                        <ng-container *ngIf="cond.fact">
                          <ng-container *ngIf="cond.fact === 'sessionsInLastNDays' || cond.fact === 'totalSessionsInLastNDays'">
                            <span class="text-xs text-gray-500 flex-shrink-0">in last</span>
                            <nz-input-number [(ngModel)]="cond.params!['days']" [nzMin]="1" style="width:72px;flex-shrink:0" nzPlaceHolder="7"></nz-input-number>
                            <span class="text-xs text-gray-500 flex-shrink-0">days</span>
                          </ng-container>
                          <ng-container *ngIf="factType(rule.action, cond.fact) === 'bool'">
                            <span class="text-xs text-gray-500 flex-shrink-0">is</span>
                            <nz-select [(ngModel)]="cond.value" style="width:80px;flex-shrink:0">
                              <nz-option [nzValue]="true" nzLabel="Yes"></nz-option>
                              <nz-option [nzValue]="false" nzLabel="No"></nz-option>
                            </nz-select>
                          </ng-container>
                          <ng-container *ngIf="factType(rule.action, cond.fact) === 'number'">
                            <nz-select [(ngModel)]="cond.operator" style="width:68px;flex-shrink:0">
                              <nz-option *ngFor="let op of NUM_OPS" [nzValue]="op.value" [nzLabel]="op.label"></nz-option>
                            </nz-select>
                            <nz-input-number [(ngModel)]="cond.value" style="width:78px;flex-shrink:0"></nz-input-number>
                          </ng-container>
                        </ng-container>
                        <button nz-button nzType="link" nzDanger nzSize="small" style="padding:0 4px;flex-shrink:0" (click)="removeCond(rule, i)">✕</button>
                      </div>
                      <div *ngIf="rule.condRows.length > 1" class="flex items-center gap-2 my-2">
                        <span class="text-xs text-gray-500">Match</span>
                        <nz-select [(ngModel)]="rule.condLogic" style="width:200px">
                          <nz-option nzValue="all" nzLabel="ALL conditions (AND)"></nz-option>
                          <nz-option nzValue="any" nzLabel="ANY condition (OR)"></nz-option>
                        </nz-select>
                      </div>
                      <button nz-button nzType="link" nzSize="small" (click)="addCond(rule)">+ Add condition</button>
                    </ng-container>
                  </div>

                  <!-- THEN zone -->
                  <div class="p-4">
                    <div class="text-xs font-bold uppercase tracking-wider mb-3" style="color:#9ca3af">THEN — outputs</div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div *ngFor="let out of outputsFor(rule.action)" [class.md:col-span-2]="out.type === 'text'">
                        <label class="block mb-1 text-xs font-medium" [class.text-gray-300]="isOutputDisabled(rule, out)" [class.text-gray-500]="!isOutputDisabled(rule, out)">{{ out.label }}</label>
                        <nz-select *ngIf="out.type === 'select'" [(ngModel)]="rule.outputs[out.key]" class="w-full" nzPlaceHolder="— not set —" [nzDisabled]="isOutputDisabled(rule, out)" (ngModelChange)="out.key === 'sessionAllowed' || out.key === 'eventAllowed' || out.key === 'diagAllowed' ? onAllowedFieldChange(rule) : null">
                          <nz-option nzValue="" nzLabel="— not set —"></nz-option>
                          <nz-option *ngFor="let opt of out.options" [nzValue]="opt.v" [nzLabel]="opt.l"></nz-option>
                        </nz-select>
                        <nz-input-number *ngIf="out.type === 'number'" [(ngModel)]="rule.outputs[out.key]" class="w-full" nzPlaceholder="not set" [nzDisabled]="isOutputDisabled(rule, out)"></nz-input-number>
                        <input *ngIf="out.type === 'text'" nz-input [(ngModel)]="rule.outputs[out.key]" placeholder="e.g. Your company's therapy sessions for this quarter have been used up." [disabled]="isOutputDisabled(rule, out)" />
                      </div>
                    </div>
                    <div class="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-100">
                      <button nz-button nzType="default" (click)="rule.expanded = false">Cancel</button>
                      <button nz-button nzType="primary" [nzLoading]="rule.saving" (click)="saveRule(rule)">Save Rule</button>
                    </div>
                  </div>

                </div>
              </div>

              <!-- Add benefit rule button -->
              <button
                class="w-full mt-2 py-2 px-4 border-dashed border rounded-lg text-sm text-gray-400 cursor-pointer hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
                style="border-color:#d9d9d9;background:transparent"
                (click)="addBenefitRule()"
              >
                + Add visibility rule
              </button>
            </div>
          </nz-spin>
        </div>
      </div>
    </nz-tab>

  </nz-tabset>
</nz-spin>

<!-- Vertical Concerns Drawer -->
<nz-drawer
  [nzVisible]="showConcernsDrawer"
  [nzWidth]="520"
  [nzTitle]="selectedVisibilityVertical?.verticalName || 'Edit Concerns'"
  (nzOnClose)="closeConcernsDrawer()"
>
  <ng-container *nzDrawerContent>
    <div *ngIf="selectedVisibilityVertical" class="flex flex-col gap-3">
      <div class="text-sm text-gray-500">
        Hide concerns under <span class="font-medium">{{ selectedVisibilityVertical.verticalName }}</span>.
        Hidden concerns won't appear for employees, even when the vertical is visible.
      </div>

      <div class="flex items-center justify-between gap-2">
        <div class="text-xs text-gray-500">
          {{ hiddenConcernsCountFor(selectedVisibilityVertical) }} hidden
          · {{ selectedVisibilityVertical.concerns.length || 0 }} total
        </div>
        <div class="flex gap-2">
          <button nz-button nzSize="small" (click)="showAllConcerns()">Show all</button>
          <button nz-button nzSize="small" (click)="hideAllConcerns()">Hide all</button>
        </div>
      </div>

      <div class="max-h-[60vh] overflow-auto border rounded">
        <div
          *ngFor="let c of selectedVisibilityVertical.concerns"
          class="flex items-center justify-between px-3 py-2 border-b last:border-b-0"
        >
          <label nz-checkbox [(ngModel)]="c.hidden" (ngModelChange)="markVisibilityDirty()">
            {{ c.concernName }}
          </label>
          <nz-tag [nzColor]="c.hidden ? 'orange' : 'default'">{{ c.hidden ? 'Hidden' : 'Visible' }}</nz-tag>
        </div>
      </div>

      <div class="flex justify-end gap-2 mt-1">
        <button nz-button (click)="closeConcernsDrawer()">Close</button>
      </div>
    </div>
  </ng-container>
</nz-drawer>
